import { Button } from "primereact/button";
import styles from "./KrogerReport.module.scss";
import { useRef, useState } from "react";
import { Calendar } from "primereact/calendar";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import {
  billbackSummaryColumns,
  createBillbackSummaryHeaderGroup
} from "../../constants/table-columns/krogerReportTableColumns";
import * as constants from "../../constants/constants";
import {
  getBillbackSummary,
  getBillbackViewRecords,
  saveBillbackSummary
} from "../../services/KrogerReportService";
import Spinner from "../../components/spinner/spinner";
import { formatInTimeZone } from "date-fns-tz";
import { Toast } from "primereact/toast";
import { ConfirmDialog } from "primereact/confirmdialog";

const BillbackSummary = () => {
  const toast = useRef(null);
  const [excludedUnits, setExcludedUnits] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [invoiceNumbers, setInvoiceNumbers] = useState("");
  const VIEWS = [
    { key: "search", label: "Search" },
    { key: "records", label: "View Records" }
  ];
  const [activeView, setActiveView] = useState("search");
  const [billbackSummaryData, setBillbackSummaryData] = useState([]);
  const [billbackTotals, setBillbackTotals] = useState(null);
  const [showSpinner, setShowSpinner] = useState(false);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [viewRecordsData, setViewRecordsData] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [showDeleteConfirmationDialog, setShowDeleteConfirmationDialog] =
    useState(false);
  const [startDateError, setStartDateError] = useState("");
  const [endDateError, setEndDateError] = useState("");

  const BUSINESS_UNITS = [
    "Fresh Kitchen Deli",
    "Fresh Kitchen Produce",
    "Retail Produce"
  ];
  const toggleUnit = (unit) =>
    setExcludedUnits((prev) =>
      prev.includes(unit) ? prev.filter((u) => u !== unit) : [...prev, unit]
    );
  const convertToPacific = (date) => {
    if (!date) return null;
    return formatInTimeZone(
      new Date(date),
      "America/Los_Angeles",
      "yyyy-MM-dd'T'HH:mm:ss.SSS"
    );
  };
  const validateDates = () => {
    let isValid = true;

    if (!startDate) {
      setStartDateError("Start date is required");
      isValid = false;
    } else {
      setStartDateError("");
    }

    if (!endDate) {
      setEndDateError("End date is required");
      isValid = false;
    } else {
      setEndDateError("");
    }

    return isValid;
  };
  const handleSearch = async () => {
    if (!validateDates()) return;
    try {
      setShowSpinner(true);
      const payload = {
        startDate: convertToPacific(startDate),
        endDate: convertToPacific(endDate),
        invoiceNumbers: invoiceNumbers
          ?.split(/[\n,]+/)
          ?.map((num) => num.trim())
          .filter(Boolean)
      };
      const response = await getBillbackSummary(payload);
      setBillbackSummaryData(response?.rows ?? []);
      setBillbackTotals(response?.totals ?? null);
      setShowSpinner(false);
    } catch (error) {
      console.error("Error searching billback summary:", error);
      setBillbackSummaryData([]);
      setBillbackTotals(null);
      setShowSpinner(false);
    }
  };

  const handleSearchInViewRecords = async () => {
    if (!validateDates()) return;
    try {
      setShowSpinner(true);
      const payload = {
        startDate: convertToPacific(startDate),
        endDate: convertToPacific(endDate)
      };
      const response = await getBillbackViewRecords(payload);
      setViewRecordsData(
        (response?.rows ?? [])?.map((row, index) => ({
          ...row,
          rowId: `${row?.date}-${row?.store}-${index}`
        }))
      );
      setBillbackTotals(response?.totals ?? null);
      setShowSpinner(false);
    } catch (error) {
      setShowSpinner(false);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: error?.message,
        life: 4000
      });
    }
  };

  const visibleColumns = billbackSummaryColumns?.filter(({ group }) => {
    if (
      excludedUnits.includes("Fresh Kitchen Deli") &&
      group === "billbackDeli"
    ) {
      return false;
    }
    if (
      excludedUnits.includes("Fresh Kitchen Produce") &&
      group === "billbackProduce"
    ) {
      return false;
    }
    if (
      excludedUnits.includes("Retail Produce") &&
      group === "billbackRetail"
    ) {
      return false;
    }
    return true;
  });

  const headerGroup = createBillbackSummaryHeaderGroup(
    visibleColumns,
    activeView === "records"
  );

  const getColumnValue = (row, column) => {
    if (!column.group) {
      return row?.[column.field] ?? "";
    }
    const groupMap = {
      billbackDeli: "deli",
      billbackProduce: "produce",
      billbackRetail: "retail"
    };
    const groupName = groupMap[column.group];
    const value = row?.[groupName]?.[column.field];
    const currencyFields = ["vendor", "logistics", "go", "totalCredit"];
    if (currencyFields.includes(column.field)) {
      return Number(value ?? 0).toLocaleString("en-US", {
        style: "currency",
        currency: "USD"
      });
    }

    return value ?? "";
  };
  const formatCurrency = (value) => {
    return Number(value ?? 0).toLocaleString("en-US", {
      style: "currency",
      currency: "USD"
    });
  };
  const getTotalValue = (column) => {
    if (!billbackTotals) {
      return "";
    }
    if (!column.group) {
      if (column.field === "date") {
        return "Grand Total";
      }
      return "";
    }
    if (column.group === "billbackDeli") {
      const value = billbackTotals?.deli?.[column.field];
      if (["vendor", "logistics", "go"].includes(column.field)) {
        return formatCurrency(value);
      }
      return "";
    }
    if (column.group === "billbackProduce") {
      const value = billbackTotals?.produce?.[column.field];
      if (["vendor", "logistics", "go"].includes(column.field)) {
        return formatCurrency(value);
      }
      return "";
    }
    if (column.group === "billbackRetail") {
      if (column.field === "totalCredit") {
        return formatCurrency(billbackTotals?.krogerRetailTotal);
      }
      return "";
    }
    return "";
  };

  const handleSaveToViewRecords = async () => {
    try {
      setShowSpinner(true);
      const invoiceIds = billbackSummaryData
        ?.map((row) => row?.invoiceId)
        ?.filter((id) => id != null);

      const uniqueInvoiceNumbers = [...new Set(invoiceIds)];

      if (uniqueInvoiceNumbers?.length === 0) {
        return;
      }
      const payload = {
        invoiceIds: uniqueInvoiceNumbers,
        isBillBacked: true
      };
      const response = await saveBillbackSummary(payload);
      setShowSpinner(false);
      toast?.current?.show({
        severity: "success",
        summary: "Success",
        detail: response,
        life: 4000
      });
    } catch (error) {
      setShowSpinner(false);
      toast?.current?.show({
        severity: "error",
        summary: "Error",
        detail: error?.message,
        life: 4000
      });
    }
  };
  const closeConfirmationDialog = () => {
    setShowConfirmationDialog(false);
  };
  const handleViewChange = (view) => {
    setActiveView(view);
    setBillbackSummaryData([]);
    setViewRecordsData([]);
    setBillbackTotals(null);
  };
  const handleDeleteSelected = async () => {
    if (!selectedRows?.length) {
      return;
    }
    try {
      setShowSpinner(true);
      const invoiceIds = selectedRows
        ?.map((row) => row?.invoiceId)
        ?.filter((id) => id != null);
      const payload = {
        invoiceIds: [...new Set(invoiceIds)],
        isBillBacked: false
      };
      const response = await saveBillbackSummary(payload);
      toast?.current?.show({
        severity: "success",
        summary: "Success",
        detail: response,
        life: 4000
      });
      setSelectedRows([]);
      handleSearchInViewRecords();
      setShowSpinner(false);
    } catch (error) {
      setShowSpinner(false);
      toast?.current?.show({
        severity: "error",
        summary: "Error",
        detail: error?.message,
        life: 4000
      });
    }
  };
  const closeDeleteConfirmationDialog = () => {
    setShowDeleteConfirmationDialog(false);
  };

  return (
    <div>
      {showSpinner && <Spinner />}
      <Toast ref={toast} position="center" />
      <div role="tablist" className={styles.subTabs}>
        {VIEWS.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            role="tab"
            id={`billback-tab-${key}`}
            aria-selected={activeView === key}
            aria-controls={`billback-panel-${key}`}
            className={`${styles.subTab} ${
              activeView === key ? styles.subTabActive : ""
            }`}
            onClick={() => handleViewChange(key)}
          >
            {label}
          </button>
        ))}
      </div>
      <div className="grid mt-2">
        <div className="col-12 md:col-3">
          <div className="mb-1">
            <label>Start Date</label>
            <span className="text-red-500">*</span>
          </div>
          <Calendar
            value={startDate}
            onChange={(e) => {
              setStartDateError("");
              setStartDate(e.value);
            }}
            showIcon
            placeholder="mm-dd-yy"
          />
          {startDateError && (
            <small className="p-error">{startDateError}</small>
          )}
        </div>
        <div className="col-12 md:col-3">
          <div className="mb-1">
            <label>End Date</label>
            <span className="text-red-500">*</span>
          </div>
          <Calendar
            value={endDate}
            onChange={(e) => {
              setEndDateError("");
              setEndDate(e.value);
            }}
            showIcon
            placeholder="mm-dd-yy"
          />
          {endDateError && <small className="p-error">{endDateError}</small>}
        </div>
        {activeView === "search" && (
          <div className="col-12 md:col-4">
            <div className="mb-1">
              <label>Invoice #(s) — one per line or comma separated</label>
            </div>
            <textarea
              className={styles.potextarea}
              id="poNumbers"
              value={invoiceNumbers}
              onChange={(e) => setInvoiceNumbers(e.target.value)}
            />
          </div>
        )}
        <div className="col-12 md:col-2">
          <div
            className={`flex flex-column gap-2 ${activeView === "search" ? "md:mt-3" : "md:mt-4"}`}
          >
            <Button
              label="Search"
              type="submit"
              size="medium"
              className={`mb-2 md:mb-0 ${styles?.primaryBtn} ${styles?.billBackBtn}`}
              onClick={
                activeView === "search"
                  ? handleSearch
                  : handleSearchInViewRecords
              }
            />
            {activeView === "search" && (
              <Button
                label="Clear"
                type="button"
                size="medium"
                className={`${styles?.resetBtn} ${styles?.billBackBtn}`}
              />
            )}
          </div>
        </div>
      </div>
      <div className={styles.filterBar}>
        {BUSINESS_UNITS?.map((unit) => {
          const active = !excludedUnits.includes(unit);
          return (
            <button
              key={unit}
              type="button"
              aria-pressed={active}
              className={`${styles.filterChip} ${active ? styles.filterChipActive : ""}`}
              onClick={() => toggleUnit(unit)}
            >
              <i className={`pi ${active ? "pi-check" : "pi-plus"}`} />
              {unit}
            </button>
          );
        })}
      </div>
      <div className="flex justify-content-between align-items-center mb-2">
        <strong>
          <p>Records Found: {billbackTotals?.invoiceCount ?? 0}</p>
        </strong>
        {activeView === "search" ? (
          <Button
            className={styles.downloadButton}
            onClick={() => setShowConfirmationDialog(true)}
            disabled={billbackSummaryData?.length <= 0}
          >
            Save to View Records
          </Button>
        ) : (
          <Button
            label="Delete"
            icon="pi pi-trash"
            severity="danger"
            onClick={() => setShowDeleteConfirmationDialog(true)}
            disabled={selectedRows?.length === 0}
          />
        )}
      </div>
      <div>
        <DataTable
          className={styles.regionDataGrid}
          value={
            activeView === "search" ? billbackSummaryData : viewRecordsData
          }
          selection={activeView === "records" ? selectedRows : null}
          onSelectionChange={(e) => setSelectedRows(e.value)}
          dataKey="rowId"
          scrollable
          scrollHeight="30vw"
          tableStyle={{
            minWidth: "50rem",
            width: "max-content"
          }}
          headerColumnGroup={headerGroup}
          paginator={constants?.paginator}
          rows={10}
          rowsPerPageOptions={constants?.rowsPerPageOptions}
          paginatorTemplate={constants?.paginatorTemplate}
          currentPageReportTemplate={constants?.currentPageReportTemplate}
          emptyMessage={constants?.emptyMessage}
          rowClassName={(row) =>
            row?.rowType === "totals" ? styles.totalRow : ""
          }
        >
          {activeView === "records" && (
            <Column
              selectionMode="multiple"
              headerStyle={{ width: "3rem", minWidth: "3rem" }}
              bodyStyle={{ width: "3rem", minWidth: "3rem" }}
              exportable={false}
              footerClassName={styles.totalRow}
            />
          )}
          {visibleColumns?.map((column) => (
            <Column
              key={`${column.group || "main"}-${column.field}`}
              field={column.field}
              header={column.header}
              sortable={column.sortable}
              style={{
                minWidth: column.minWidth
              }}
              body={(rowData) => getColumnValue(rowData, column)}
              bodyClassName={
                column.group === "billbackRetail" ? "retailDataCell" : ""
              }
              footer={getTotalValue(column)}
              footerClassName={`${styles.totalRow} ${column.group === "billbackRetail" ? "retailDataCell" : ""}`}
            />
          ))}
        </DataTable>
      </div>
      <ConfirmDialog
        visible={showConfirmationDialog}
        onHide={closeConfirmationDialog}
        message={"Are you sure you want to save the records to view records?"}
        header="Confirmation"
        icon="pi pi-exclamation-triangle"
        accept={handleSaveToViewRecords}
        reject={closeConfirmationDialog}
      />
      <ConfirmDialog
        visible={showDeleteConfirmationDialog}
        onHide={closeDeleteConfirmationDialog}
        message={"Are you sure you want to delete the records?"}
        header="Confirmation"
        icon="pi pi-exclamation-triangle"
        accept={handleDeleteSelected}
        reject={closeDeleteConfirmationDialog}
      />
    </div>
  );
};

export default BillbackSummary;
